// src/common/components/navigation/shared/useNavButtons.ts
import { useMemo } from "react";
import {
  MdLocalOffer,
  MdPerson,
  MdLogin,
  MdContacts,
  MdLocalShipping,
  MdCardGiftcard,
  MdCategory,
  MdQrCodeScanner,
  MdClose,
} from "react-icons/md";
import { ROUTES } from "../../utils/constants";
import { getClusterTypeIcon } from "../../utils/functions/enumToIcon";
import { getMainScreenVariant } from "../../../config/runtimeConfig";
import { useAuthRoute } from "../../hooks/useAuthRoute";
import { ButtonConfig } from "../../types/types";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../../store/slices/currentUserSlice";
import { selectCurrentCluster } from "../../../store/slices/currentClusterSlice";
import {
  selectIsExecutor,
  selectIsAdmin,
  selectClientUserId,
} from "../../../store/slices/executorSlice";
import { store } from "../../../store/store";
import { openServiceModal } from "../../../store/slices/commonModalSlice";
import { clearClientUserId } from "../../../store/slices/executorSlice";
import { reloadUserData } from "../../utils/functions/reloadUserData";

export const useDesktopButtons = () => {
  const getAuthRoute = useAuthRoute();
  const showCatalogButton = getMainScreenVariant() === "products";
  const currentUser = useSelector(selectCurrentUser);
  const currentCluster = useSelector(selectCurrentCluster);
  const clusterIcon = getClusterTypeIcon(currentCluster?.type);
  const isExecutor = useSelector(selectIsExecutor);
  const isAdmin = useSelector(selectIsAdmin);
  const clientUserId = useSelector(selectClientUserId);
  const canServe = isExecutor || isAdmin;

  return useMemo(() => {
    const buttons: ButtonConfig[] = [
      {
        path: ROUTES.MAIN,
        icon: clusterIcon,
        label: "Главная",
      },
    ];

    if (showCatalogButton) {
      buttons.push({
        path: ROUTES.CATEGORY,
        icon: MdCategory,
        label: "Каталог",
      });
    }

    buttons.push(
      {
        path: ROUTES.PROMO,
        icon: MdLocalOffer,
        label: "Акции",
      },
      currentUser
        ? {
            path: ROUTES.PROFILE,
            icon: MdPerson,
            label: "Профиль",
          }
        : {
            path: getAuthRoute(),
            icon: MdLogin,
            label: "Войти",
          },
      currentUser && canServe
        ? clientUserId
          ? {
              path: "",
              icon: MdClose,
              label: "Завершить",
              onClick: () => {
                store.dispatch(clearClientUserId());
                reloadUserData();
              },
            }
          : {
              path: "",
              icon: MdQrCodeScanner,
              label: "Обслуживание",
              onClick: () => store.dispatch(openServiceModal()),
            }
        : {
            path: ROUTES.CONTACTS,
            icon: MdContacts,
            label: "Контакты",
          },
    );

    if (currentCluster?.withDelivery) {
      buttons.push({
        path: ROUTES.DELIVERY,
        icon: MdLocalShipping,
        label: "Доставка",
      });
    }

    if (currentCluster?.withBonuses) {
      buttons.push({
        path: ROUTES.BONUS,
        icon: MdCardGiftcard,
        label: "Бонусы",
      });
    }

    return buttons;
  }, [
    currentUser,
    currentCluster,
    clusterIcon,
    showCatalogButton,
    getAuthRoute,
    canServe,
    clientUserId,
  ]);
};

export const useMobileButtons = () => {
  const getAuthRoute = useAuthRoute();
  const currentUser = useSelector(selectCurrentUser);
  const currentCluster = useSelector(selectCurrentCluster);
  const clusterIcon = getClusterTypeIcon(currentCluster?.type);
  const isExecutor = useSelector(selectIsExecutor);
  const isAdmin = useSelector(selectIsAdmin);
  const clientUserId = useSelector(selectClientUserId);
  const canServe = isExecutor || isAdmin;

  return useMemo(
    () => [
      {
        path: ROUTES.MAIN,
        icon: clusterIcon,
        label: "Главная",
      },
      {
        path: ROUTES.PROMO,
        icon: MdLocalOffer,
        label: "Акции",
      },
      currentUser
        ? {
            path: ROUTES.PROFILE,
            icon: MdPerson,
            label: "Профиль",
          }
        : {
            path: getAuthRoute(),
            icon: MdLogin,
            label: "Войти",
          },
      currentUser && canServe
        ? clientUserId
          ? {
              path: "",
              icon: MdClose,
              label: "Завершить",
              onClick: () => {
                store.dispatch(clearClientUserId());
                reloadUserData();
              },
            }
          : {
              path: "",
              icon: MdQrCodeScanner,
              label: "Обслуживание",
              onClick: () => store.dispatch(openServiceModal()),
            }
        : {
            path: ROUTES.CONTACTS,
            icon: MdContacts,
            label: "Контакты",
          },
    ],
    [currentUser, clusterIcon, getAuthRoute, canServe, clientUserId],
  );
};
